import { Text } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { Stack } from "@mui/material";
import { formatCentsAsUsd } from "@src/appV2/lib/Money/utils/currency";

import { Button } from "../../../components/Button";
import { DialogFooter } from "../../../components/DialogFooter";
import { InstantPayIndicator } from "../../../WorkerShifts/components/InstantPayIndicator";
import { formatApproximatePayInCentsAsUsd } from "../utils";

interface ShiftBundleBottomSheetFooterProps {
  requiredShiftCount: number;
  totalShiftCount: number;
  totalPayInMinorUnits: number | undefined;
  isPayExact: boolean;
  isInstantPay: boolean;
  isBookable: boolean;
  onSelectShifts: () => void;
}

function getButtonText(
  isBookable: boolean,
  requiredShiftCount: number,
  totalShiftCount: number
): string {
  if (!isBookable) {
    return "Can't book";
  }

  if (requiredShiftCount === totalShiftCount) {
    return "Book";
  }

  return "Select shifts";
}

function getFormattedTotalPay(totalPayInMinorUnits: number, isPayExact: boolean) {
  return isPayExact
    ? formatCentsAsUsd(totalPayInMinorUnits)
    : formatApproximatePayInCentsAsUsd(totalPayInMinorUnits);
}

export function ShiftBundleBottomSheetFooter(props: ShiftBundleBottomSheetFooterProps) {
  const {
    requiredShiftCount,
    totalShiftCount,
    totalPayInMinorUnits,
    isInstantPay,
    isPayExact,
    isBookable,
    onSelectShifts,
  } = props;

  const formattedTotalPay = isDefined(totalPayInMinorUnits)
    ? getFormattedTotalPay(totalPayInMinorUnits, isPayExact)
    : "";
  const footerText =
    requiredShiftCount === totalShiftCount
      ? `${formattedTotalPay} total`
      : `Up to ${formattedTotalPay}`;

  return (
    <DialogFooter withShadow orientation="vertical">
      <Stack direction="row" sx={{ width: "100%", justifyContent: "space-between" }}>
        <Stack spacing={3} sx={{ paddingLeft: 5 }}>
          {isDefined(totalPayInMinorUnits) && (
            <Text semibold variant="h4">
              {footerText}
            </Text>
          )}
          {isInstantPay && <InstantPayIndicator />}
        </Stack>

        <Button
          disabled={!isBookable}
          variant="contained"
          size="large"
          sx={{ minWidth: "30%" }}
          onClick={onSelectShifts}
        >
          {getButtonText(isBookable, requiredShiftCount, totalShiftCount)}
        </Button>
      </Stack>
    </DialogFooter>
  );
}
